import { useEffect, useState } from 'react';

import { cx } from '@emotion/css';
import { Col, Popover, Row } from 'antd';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { doAddNewAccountType, doClearSwitchAccount, doSucceededGetAccount, doSwitchAccount } from 'src/actions';
import {
  useAccountSelector,
  useAuthenticatedUserSelector,
  useCanCreateAccount,
  useCanSwitchAccount,
  useFindOtherAccountList,
} from 'src/hooks';
import { Account, AccountStatus } from 'src/models';
import { MAIN_PATH } from 'src/pages/Main';
import { HISTORY_PATH } from 'src/pages/Main/pages/History/History';
import { SUMMARY_PATH } from 'src/pages/Main/pages/Summary/Summary';
import { BaseStyle, Color, Spacing } from 'src/styles';
import { logo } from 'src/styles/images';
import { Maybe } from 'src/utils';

import { MButton } from '../FormComponents/MButton/MButton';
import { MDivider } from '../MDivider/MDivider';

import { AccountHolderFullName } from './AccountHolderFullName';
import { AccountMenuItem } from './AccountMenuItem';
import { AccountStatusBanner } from './AccountStatusBanner';
import { AccountValue } from './AccountValue';
import { getAccountSettingMenuItemList, getUserSettingMenuItemList } from './constants';
import './index.css';
import * as Styles from './Header.styles';
import { LogoutMenuItem } from './LogoutMenuItem';
import { MenuItem } from './MenuItem';
import ResourceLinks from './ResourceLinks';
import { UserInfo } from './UserInfo';

export type DesktopProps = {
  authToken?: string;
  pathName: string;
  status?: AccountStatus;
};

export const DesktopHeader = ({ authToken, pathName, status }: DesktopProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSwitchLoading: boolean = useSelector((state: any) => state.accounts.switch.isLoading);
  const isSwitchSuccess: boolean = useSelector((state: any) => Boolean(state.accounts.switch.__succeeded));
  const switchAccount: Maybe<Account> = useSelector((state: any) => state.accounts.switch.data);

  const [switchToAccountId, setSwitchToAccountId] = useState<string | null>(null);

  const [visible, setIsVisible] = useState(false);
  const [step, setStep] = useState<'account' | 'switch'>('account');

  const { isUserLoading } = useAuthenticatedUserSelector();
  const { account } = useAccountSelector();
  const canCreateAccount = useCanCreateAccount();
  const canSwitchAccount = useCanSwitchAccount();
  const otherAccountList = useFindOtherAccountList();

  const switchTo = (account: Account) => {
    setSwitchToAccountId(account.id);
    dispatch(doSwitchAccount({ params: { id: account.id } }));
  };

  const handleVisibleChange = (visible: boolean) => {
    setIsVisible(visible);
  };

  const onHide = () => {
    setIsVisible(false);
  };

  const onGoToSwitchStep = () => {
    setStep('switch');
  };

  const onGoBackToAccountStep = () => {
    setStep('account');
  };

  const onCreateAccount = () => {
    dispatch(doAddNewAccountType());
    setIsVisible(false);
    navigate('/accounts/personal-information');
  };

  const onCurrentAccount = () => {
    setIsVisible(false);
  };

  const renderOtherAccountList = () => {
    if (isEmpty(otherAccountList)) return null;

    return (
      <>
        <Row>
          <MDivider />
        </Row>
        <Row>
          <Col span={24}>
            <span className={Styles.menuSubtitle}>OTHER ACCOUNTS</span>
          </Col>
          {otherAccountList.map(acc => (
            <Col span={24} key={acc.id}>
              <AccountMenuItem
                account={acc}
                onClick={() => switchTo(acc)}
                isLoading={switchToAccountId === acc.id && (isSwitchLoading || isUserLoading)}
              />
            </Col>
          ))}
        </Row>
      </>
    );
  };

  const renderAccountStep = () => {
    return (
      <>
        <Row justify='center' align='middle' className={Spacing.mb12}>
          <Col span={24} className={cx(Spacing.mb4, BaseStyle.level)}>
            <AccountHolderFullName account={account} alignment='center' />
          </Col>
          <Col span={24} className={Spacing.my4}>
            {account?.accountId && <AccountValue />}
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <MenuItem
              name='My Account'
              path={
                account?.isEntityAccountTypeActive ? '/accounts/entity-information' : '/accounts/personal-information'
              }
              onClick={onHide}
            />
          </Col>
        </Row>
        {canSwitchAccount && (
          <Row>
            <Col span={24}>
              <MenuItem
                name='Switch Account'
                endIcon={<i className='ri-arrow-right-s-line' style={{ fontSize: 24 }} />}
                activatable={false}
                onClick={onGoToSwitchStep}
              />
            </Col>
          </Row>
        )}
        {canCreateAccount && (
          <Row>
            <Col span={24}>
              <MenuItem
                startIcon={<i className='ri-user-add-line' style={{ fontSize: 24 }} />}
                name='Add New Account Type'
                path='/accounts/personal-information'
                activatable={false}
                onClick={onCreateAccount}
              />
            </Col>
          </Row>
        )}
        {account?.accountId && (
          <>
            <Row>
              <MDivider />
            </Row>
            <Row>
              <Col span={24}>
                <span className={Styles.menuSubtitle}>ACCOUNT SETTINGS</span>
              </Col>
              <Col span={24}>
                {getAccountSettingMenuItemList(account).map(aMenuItem => (
                  <MenuItem
                    key={aMenuItem.key}
                    name={aMenuItem.name}
                    path={aMenuItem.path(account?.id)}
                    startIcon={aMenuItem.startIcon?.()}
                    endIcon={aMenuItem.endIcon?.(status)}
                    onClick={onHide}
                  />
                ))}
              </Col>
            </Row>
          </>
        )}
        <Row>
          <MDivider />
        </Row>
        <Row>
          <Col span={24}>
            <span className={Styles.menuSubtitle}>USER SETTINGS</span>
          </Col>
          <Col span={24}>
            {getUserSettingMenuItemList(account).map(aMenuItem => (
              <MenuItem key={aMenuItem.key} name={aMenuItem.name} path={aMenuItem.path(account?.id)} onClick={onHide} />
            ))}
          </Col>
        </Row>
      </>
    );
  };

  const renderSwitchStep = () => {
    return (
      <>
        <Row>
          <Col span={24}>
            <MenuItem
              startIcon={<i className='ri-arrow-left-line' />}
              name='Accounts'
              activatable={false}
              onClick={onGoBackToAccountStep}
            />
          </Col>
        </Row>
        <Row>
          <MDivider />
        </Row>
        <Row>
          <Col span={24}>
            <span className={Styles.menuSubtitle}>CURRENT ACCOUNT</span>
          </Col>
          <Col span={24}>
            <AccountMenuItem account={account} onClick={onCurrentAccount} />
          </Col>
        </Row>
        {renderOtherAccountList()}
      </>
    );
  };

  useEffect(() => {
    if (isSwitchSuccess && switchAccount) {
      setIsVisible(false);
      dispatch(doSucceededGetAccount(switchAccount));
      navigate('/main/summary');
    }

    return () => {
      dispatch(doClearSwitchAccount());
    };
  }, [isSwitchSuccess]);

  return (
    <div>
      {authToken ? (
        <div className='resourceSection'>
          <div className='resourceWidthRestriction'>
            <div>
              <ResourceLinks className='linksSmall' isAuthenticated />
            </div>
          </div>
        </div>
      ) : null}
      <Row justify='center'>
        <Col span={24}>
          <div className='mainHeader'>
            <div className='headerWidthRestriction'>
              <Link to='/'>
                <img src={logo} alt='My IPO' className='logo' />
              </Link>
              {!authToken ? (
                <div className='signedOutLinks'>
                  <ResourceLinks className='linksMedium' />
                  <div className='authOptions'>
                    <div>
                      <MButton type='secondary' onClick={() => navigate('/register')} testId={'home-btn-signup'}>
                        Sign up
                      </MButton>
                    </div>
                    <div className='authBtnContainer'>
                      <MButton type='secondary' onClick={() => navigate('/login')} testId={'home-btn-login'}>
                        Log in
                      </MButton>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='headerLinkSection'>
                  <div
                    style={{
                      display: 'inline-block',
                      margin: '0 auto',
                    }}>
                    <NavLink
                      to={`/${MAIN_PATH}/${SUMMARY_PATH}`}
                      className={pathName.indexOf(`/${SUMMARY_PATH}`) > 0 ? 'linksLargeActive' : 'linksLarge'}>
                      Summary
                    </NavLink>

                    <NavLink
                      to='/offers'
                      className={pathName.startsWith('/offers') ? 'linksLargeActive' : 'linksLarge'}>
                      Offers
                    </NavLink>

                    <NavLink
                      to={`/${MAIN_PATH}/${HISTORY_PATH}`}
                      className={pathName.indexOf(`/${HISTORY_PATH}`) > 0 ? 'linksLargeActive' : 'linksLarge'}>
                      History
                    </NavLink>
                  </div>
                  <Popover
                    content={
                      <div
                        style={{
                          width: 345,
                          backgroundColor: Color.GRAYSCALE.GRAY1,
                          borderRadius: 12,
                          paddingTop: 22,
                          boxShadow: '0 4px 16px rgba(2, 85, 163, 0.15)',
                        }}
                        data-testid={'summary-dropdown-account-menu'}>
                        {step === 'account' ? renderAccountStep() : renderSwitchStep()}
                        <Row>
                          <MDivider />
                        </Row>
                        <Row className={cx(Spacing.pl0, Spacing.pb12)}>
                          <Col span={24}>
                            <LogoutMenuItem onClick={onHide} />
                          </Col>
                        </Row>
                      </div>
                    }
                    trigger='click'
                    open={visible}
                    onOpenChange={handleVisibleChange}
                    placement='bottomRight'>
                    <UserInfo
                      onClick={() => {
                        setStep('account');
                        handleVisibleChange(!visible);
                      }}
                    />
                  </Popover>
                </div>
              )}
            </div>
          </div>
        </Col>
        <AccountStatusBanner />
      </Row>
    </div>
  );
};
